import { useEffect, useRef } from "react";
import { computeRms } from "../audio/rms";

// VAD (spec: docs/superpowers/specs/2026-07-07-m3-continuous-conversation-requirements.md §7 D7.1 / D7.5):
// ?vad-debug=1 で表示される dev overlay。
// RMS 波形 (Canvas 2D + rAF) + 現 VAD state。dev-only、production では render しない。
// continuous mode 統合: union は useContinuousMode の VadUiState と揃える (ai-speaking を含む) — coupling は避けたいので
// 直接 import せず string literal union を複製する。ズレたら typecheck で検出される (ChatScreen 経由)。
type Props = {
  stream: MediaStream | null;
  vadState: "idle" | "listening" | "hearing" | "ai-speaking" | "processing";
};

export function VadDebugOverlay({ stream, vadState }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!stream) return;
    const audioCtx = new AudioContext();
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    const buffer = new Float32Array(analyser.frequencyBinCount);
    let raf = 0;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");

    const draw = () => {
      if (!canvas || !ctx) return;
      analyser.getFloatTimeDomainData(buffer);
      const rms = computeRms(buffer);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "#22c55e";
      const barWidth = Math.min(canvas.width, rms * canvas.width * 4);
      ctx.fillRect(0, 0, barWidth, canvas.height);
      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      source.disconnect();
      void audioCtx.close();
    };
  }, [stream]);

  return (
    <div className="fixed bottom-2 right-2 bg-black/80 text-white text-xs p-2 rounded shadow-lg z-50 font-mono">
      <div>vadState: {vadState}</div>
      <div>{stream ? "" : "no stream"}</div>
      <canvas ref={canvasRef} width={200} height={20} className="mt-1 block bg-slate-900" />
    </div>
  );
}
